import React from "react";
import { motion } from "framer-motion";
import {
  BarChart3,
  TrendingUp,
  CheckCircle,
  Shield,
  Globe,
  Award,
  ArrowRight,
  Target,
  Zap,
  AlertCircle,
  Lightbulb,
  Gauge,
  Droplets,
  Leaf,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const CDP_Certification_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1a3a6b] via-[#2d5fa8] to-[#3d7fd4] text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <BarChart3 size={18} className="text-indigo-300" />
            <span>Climate Action and Environmental Leadership</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5">
            CDP Certification
          </h1>
          <p className="text-gray-100 text-base md:text-lg max-w-2xl mx-auto">
            Measure, disclose, and manage your environmental impacts including
            carbon emissions, water use, and deforestation in a transparent and
            accountable way.
          </p>
        </motion.div>
      </section>

      {/* What Is */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              What Is CDP Certification?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                CDP (Carbon Disclosure Project) certification is a global
                non-profit platform helping businesses disclose their
                environmental impacts and manage climate-related risks and
                opportunities. It enables organizations to measure and report
                environmental data in transparent, accountable ways.
              </p>
              <p>
                CDP certification focuses on three main environmental impact
                areas:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  "Carbon emissions (Scope 1, 2, 3)",
                  "Water usage and security",
                  "Deforestation and forest risks",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle
                      size={20}
                      className="text-indigo-600 mt-0.5 flex-shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                CDP provides a trusted reporting platform for climate action,
                water security, and forest risk management aligned with global
                sustainability goals.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Key Features of CDP Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Zap,
                title: "Climate Change Reporting",
                desc: "Measure and report carbon footprint including Scopes 1, 2, 3 with carbon reduction strategies.",
              },
              {
                icon: Droplets,
                title: "Water Security Reporting",
                desc: "Measure water usage, assess water scarcity risks, and implement conservation strategies.",
              },
              {
                icon: Leaf,
                title: "Forest Risk Reporting",
                desc: "Assess and manage supply chain impacts related to forests, land use, and agricultural practices.",
              },
              {
                icon: BarChart3,
                title: "Supply Chain Engagement",
                desc: "Engage suppliers in sustainable practices ensuring stakeholder contribution to environmental goals.",
              },
              {
                icon: Target,
                title: "Transparent Data Disclosure",
                desc: "Provide framework for transparent environmental reporting enhancing stakeholder trust.",
              },
              {
                icon: Award,
                title: "Climate Risk Management",
                desc: "Identify and address climate-related risks and opportunities in business strategy.",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg hover:border-l-4 hover:border-indigo-500 transition-all"
                >
                  <Icon size={32} className="text-indigo-600 mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-gray-900">
              Why CDP Certification Matters for Your Business
            </h2>
            <div className="space-y-6">
              {[
                {
                  title: "Global Climate Action Compliance",
                  desc: "Aligns business with global climate action goals and positions you as a climate risk management leader.",
                },
                {
                  title: "Market Demand for Sustainability",
                  desc: "Meets consumer, investor, and regulator demands for environmental impact disclosure.",
                },
                {
                  title: "Regulatory Compliance",
                  desc: "Provides framework for compliance with climate reporting regulations and environmental policies.",
                },
                {
                  title: "Investor Confidence",
                  desc: "Demonstrates active climate-related risk management to financial institutions and investors.",
                },
                {
                  title: "Enhanced Brand Reputation",
                  desc: "Strengthens brand reputation as responsible corporate citizen committed to environmental stewardship.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-4 p-4 bg-indigo-50 rounded-lg border-l-4 border-indigo-500"
                >
                  <Star
                    size={24}
                    className="text-indigo-600 flex-shrink-0 mt-1"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 px-4 bg-gradient-to-br from-indigo-50 to-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Benefits of CDP Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: TrendingUp,
                title: "Improved Environmental Performance",
                desc: "Track, measure, and reduce carbon emissions, water usage, and deforestation impacts.",
              },
              {
                icon: Award,
                title: "Attracting Eco-Conscious Consumers",
                desc: "Demonstrates commitment to reducing environmental impact enhancing consumer trust.",
              },
              {
                icon: Globe,
                title: "Access to Investment Opportunities",
                desc: "Signals ESG criteria alignment to investors seeking companies engaged in climate action.",
              },
              {
                icon: Shield,
                title: "Enhanced Risk Management",
                desc: "Better anticipate and mitigate financial impacts of climate change and environmental risks.",
              },
              {
                icon: CheckCircle,
                title: "Regulatory Compliance",
                desc: "Compliance with local and international climate regulations reducing legal risks.",
              },
              {
                icon: Gauge,
                title: "Competitive Advantage",
                desc: "Gains competitive edge in markets where environmental sustainability is increasingly important.",
              },
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center"
                >
                  <Icon size={36} className="text-indigo-600 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-10 text-gray-900">
              How to Achieve CDP Certification
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Sign Up for CDP Reporting",
                  desc: "Register on CDP platform to begin reporting and access CDP tools and resources.",
                },
                {
                  step: "2",
                  title: "Measure Your Environmental Impact",
                  desc: "Assess carbon emissions, water usage, and deforestation impact with data from all departments.",
                },
                {
                  step: "3",
                  title: "Develop Sustainability Action Plans",
                  desc: "Create actionable plans to reduce emissions, conserve water, and minimize environmental impacts.",
                },
                {
                  step: "4",
                  title: "Submit Your CDP Report",
                  desc: "Complete CDP reporting form and submit sustainability data for evaluation.",
                },
                {
                  step: "5",
                  title: "Audit and Verification",
                  desc: "Undergo third-party verification of environmental data, especially for higher certifications.",
                },
                {
                  step: "6",
                  title: "Continuous Improvement and Monitoring",
                  desc: "Regularly monitor environmental performance and report progress annually.",
                },
              ].map((process, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-6 bg-indigo-50 rounded-lg"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{process.step}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      {process.title}
                    </h3>
                    <p className="text-gray-700">{process.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Industries Benefiting from CDP Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Energy and Utilities",
              "Manufacturing and Industrial",
              "Transportation and Logistics",
              "Technology and Communications",
              "Financial Services",
              "Retail and Consumer Goods",
            ].map((industry, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white rounded-lg p-6 shadow-md border-l-4 border-indigo-500 text-center"
              >
                <BarChart3 size={32} className="text-indigo-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-[#1a3a6b] via-[#2d5fa8] to-[#3d7fd4] rounded-2xl p-10 text-white text-center shadow-lg"
          >
            <BarChart3 size={40} className="mx-auto mb-4 text-indigo-300" />
            <h3 className="text-2xl font-bold mb-3">
              Why Choose MBA Solutions for CDP Certification?
            </h3>
            <p className="text-indigo-100 mb-6 text-sm leading-relaxed">
              MBA Solutions provides expert CDP certification consultancy,
              guiding businesses through the reporting process to improve
              sustainability performance and enhance environmental transparency.
            </p>
            <div className="bg-white/10 rounded-lg p-4 mb-6 text-left space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-indigo-300 flex-shrink-0"
                />
                Carbon Footprint and Emissions Assessment
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-indigo-300 flex-shrink-0"
                />
                Water Usage and Risk Management Strategies
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-indigo-300 flex-shrink-0"
                />
                Forest Impact and Supply Chain Management
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-indigo-300 flex-shrink-0"
                />
                Sustainability Action Plan Development
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-indigo-300 flex-shrink-0"
                />
                Ongoing Monitoring and Reporting Support
              </p>
            </div>
            <div className="space-y-2 text-sm mb-6">
              <p>
                📧 Email:{" "}
                <a
                  href="mailto:atif@mbasolutions.co"
                  className="underline hover:text-indigo-200"
                >
                  atif@mbasolutions.co
                </a>
              </p>
              <p>
                🌐 Website:{" "}
                <a
                  href="https://www.mbasolutions.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-indigo-200"
                >
                  www.mbasolutions.co
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-white text-indigo-700 font-bold hover:bg-indigo-50 transition-all"
            >
              Get Started <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

const Star = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <polygon points="12 2 15.09 9.26 23 9.26 17.19 14.31 19.29 21.74 12 16.69 4.71 21.74 6.81 14.31 1 9.27 8.91 9.26 12 2" />
  </svg>
);

export default CDP_Certification_Details;
